import React, { useState } from 'react';
import { fmtDec, MetricCard } from '../utils';

function exportQuarterToCSV(q, year) {
  const rows = [];
  rows.push(['JG Graphics - Sales Tax Export']);
  rows.push([q.quarter + ' ' + year]);
  rows.push([]);
  rows.push(['Quarter Summary']);
  rows.push(['Taxable Subtotal', q.totalSubtotal.toFixed(2)]);
  rows.push(['Tax Collected', q.totalTax.toFixed(2)]);
  rows.push(['Effective Rate', q.totalSubtotal > 0 ? (q.totalTax / q.totalSubtotal * 100).toFixed(2) + '%' : '0%']);
  rows.push([]);

  q.buckets.forEach(bucket => {
    rows.push(['Tax Rate: ' + bucket.rateBucket]);
    rows.push(['Order #', 'Order Nickname', 'Rep', 'Payment Date', 'Subtotal', 'Tax']);
    const sorted = [...bucket.orders].sort((a,b) => new Date(a.invoiceDate) - new Date(b.invoiceDate));
    sorted.forEach(o => {
      rows.push(['#' + o.orderNumber, o.nickname, o.owner, o.paymentDate || o.paymentDate || o.invoiceDate, o.subTotal.toFixed(2), o.taxAmt.toFixed(2)]);
    });
    rows.push(['', '', '', 'Bucket Total', bucket.subtotal.toFixed(2), bucket.taxTotal.toFixed(2), bucket.rateBucket]);
    rows.push([]);
  });

  const csv = rows.map(r =>
    r.map(cell => {
      const s = String(cell == null ? '' : cell);
      return (s.includes(',') || s.includes('"') || s.includes('\n')) ? '"' + s.replace(/"/g, '""') + '"' : s;
    }).join(',')
  ).join('\r\n');

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'JG-Graphics-Sales-Tax-' + q.quarter + '-' + year + '.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function SalesTax({ data }) {
  const { salesTaxData } = data;
  const [expandedBucket, setExpandedBucket] = useState(null);
  const [expandedQuarter, setExpandedQuarter] = useState({Q1:true,Q2:true,Q3:true,Q4:true});

  if (!salesTaxData || salesTaxData.quarters.length === 0) {
    return (
      <div>
        <div className="rep-welcome"><h2>Sales tax</h2><p style={{ color: 'var(--text3)' }}>No tax data available yet.</p></div>
      </div>
    );
  }

  const { quarters, totalTax, totalSubtotal } = salesTaxData;
  const effectiveRate = totalSubtotal > 0 ? (Math.round(totalTax / totalSubtotal * 400) / 4).toFixed(2) : '—';

  return (
    <div>
      <div className="rep-welcome">
        <h2>Sales tax</h2>
        <p>Cash basis — grouped by quarter and tax rate. Rate derived from Tax ÷ Subtotal × 100.</p>
      </div>

      <div className="metrics">
        <MetricCard label="Total tax collected" value={fmtDec(totalTax)} sub="2026 YTD" valueStyle={{ color: 'var(--text)' }} />
        <MetricCard label="Total taxable subtotal" value={fmtDec(totalSubtotal)} sub="2026 YTD" />
        <MetricCard label="Effective rate" value={effectiveRate + '%'} sub="Overall blended rate" />
        <MetricCard label="Quarters with tax" value={quarters.length} sub="of 4 quarters" />
      </div>

      {quarters.map(q => (
        <div key={q.quarter} style={{ marginBottom: 20 }}>
          {/* Quarter header */}
          <div
            onClick={() => setExpandedQuarter(prev => ({ ...prev, [q.quarter]: !prev[q.quarter] }))}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 16px', background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: expandedQuarter[q.quarter] ? 'var(--radius) var(--radius) 0 0' : 'var(--radius)',
              marginBottom: expandedQuarter[q.quarter] ? 0 : 8, boxShadow: 'var(--shadow)',
              cursor: 'pointer', userSelect: 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 20, height: 20, borderRadius: 4, background: 'var(--brand-light)', color: 'var(--brand)',
                fontSize: 10, fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                transform: expandedQuarter[q.quarter] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s',
              }}>▼</div>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{q.quarter} — {new Date().getFullYear()}</span>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', fontSize: 13 }}>
              <button
                onClick={e => { e.stopPropagation(); exportQuarterToCSV(q, new Date().getFullYear()); }}
                style={{ fontSize: 11, fontWeight: 500, padding: '4px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--brand)', color: 'var(--brand)', background: 'var(--brand-light)', cursor: 'pointer', fontFamily: 'var(--font)', display: 'flex', alignItems: 'center', gap: 4 }}
              >⬇ Export</button>
              <span style={{ color: 'var(--text2)' }}>Taxable subtotal: <strong style={{ color: 'var(--text)' }}>{fmtDec(q.totalSubtotal)}</strong></span>
              <span style={{ color: 'var(--text2)' }}>Tax collected: <strong style={{ color: 'var(--text)' }}>{fmtDec(q.totalTax)}</strong></span>
              <span style={{ color: 'var(--text2)' }}>Rate: <strong style={{ color: 'var(--brand)' }}>{q.totalSubtotal > 0 ? (q.totalTax / q.totalSubtotal * 100).toFixed(2) + '%' : '—'}</strong></span>
            </div>
          </div>

          {/* Rate buckets */}
          {expandedQuarter[q.quarter] && q.buckets.map((bucket, bi) => {
            const key = q.quarter + '-' + bucket.rateBucket;
            const isExp = expandedBucket === key;
            return (
              <div key={bi} style={{ marginLeft: 16, marginBottom: 6 }}>
                <div
                  onClick={() => setExpandedBucket(isExp ? null : key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                    background: isExp ? 'var(--brand-light)' : 'var(--surface2)',
                    border: '1px solid ' + (isExp ? 'var(--brand)' : 'var(--border)'),
                    borderRadius: 'var(--radius-sm)', cursor: 'pointer', userSelect: 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    width: 20, height: 20, borderRadius: 4, background: 'var(--brand-light)',
                    color: 'var(--brand)', fontSize: 10, fontWeight: 700, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transform: isExp ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s',
                  }}>▼</div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{
                      background: 'var(--brand)', color: 'white', fontSize: 11, fontWeight: 600,
                      padding: '2px 10px', borderRadius: 20,
                    }}>{bucket.rateBucket}</span>
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>{bucket.orders.length} order{bucket.orders.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 24, fontSize: 12 }}>
                    <span style={{ color: 'var(--text2)' }}>Subtotal: <strong style={{ color: 'var(--text)', fontFamily: 'var(--mono)' }}>{fmtDec(bucket.subtotal)}</strong></span>
                    <span style={{ color: 'var(--text2)' }}>Tax: <strong style={{ color: 'var(--text)', fontFamily: 'var(--mono)' }}>{fmtDec(bucket.taxTotal)}</strong></span>
                  </div>
                </div>

                {isExp && (
                  <div style={{
                    border: '1px solid var(--brand)', borderTop: 'none',
                    borderBottomLeftRadius: 'var(--radius-sm)', borderBottomRightRadius: 'var(--radius-sm)',
                    background: 'var(--surface)', overflow: 'hidden', marginLeft: 0,
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: 'var(--surface2)' }}>
                          <th style={{ padding: '8px 16px', textAlign: 'left', color: 'var(--text3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Order #</th>
                          <th style={{ padding: '8px 16px', textAlign: 'left', color: 'var(--text3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Order nickname</th>
                          <th style={{ padding: '8px 16px', textAlign: 'left', color: 'var(--text3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Rep</th>
                          <th style={{ padding: '8px 16px', textAlign: 'right', color: 'var(--text3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Invoice date</th>
                          <th style={{ padding: '8px 16px', textAlign: 'right', color: 'var(--text3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Subtotal</th>
                          <th style={{ padding: '8px 16px', textAlign: 'right', color: 'var(--text3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tax</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bucket.orders.sort((a,b) => new Date(a.invoiceDate) - new Date(b.invoiceDate)).map((order, oi) => (
                          <tr key={oi} style={{ borderTop: '1px solid var(--border)' }}>
                            <td style={{ padding: '8px 16px', fontFamily: 'var(--mono)', fontWeight: 500, color: 'var(--brand)' }}>#{order.orderNumber}</td>
                            <td style={{ padding: '8px 16px', color: 'var(--text)', maxWidth: 260, whiteSpace: 'normal', fontSize: 11 }}>{order.nickname}</td>
                            <td style={{ padding: '8px 16px', color: 'var(--text2)', fontSize: 11 }}>{order.owner}</td>
                            <td style={{ padding: '8px 16px', fontFamily: 'var(--mono)', textAlign: 'right', color: 'var(--text3)', fontSize: 11 }}>{order.paymentDate || order.invoiceDate}</td>
                            <td style={{ padding: '8px 16px', fontFamily: 'var(--mono)', textAlign: 'right', color: 'var(--text)' }}>{fmtDec(order.subTotal)}</td>
                            <td style={{ padding: '8px 16px', fontFamily: 'var(--mono)', textAlign: 'right', color: 'var(--text)', fontWeight: 500 }}>{fmtDec(order.taxAmt)}</td>
                          </tr>
                        ))}
                        {/* Bucket totals row */}
                        <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--surface2)' }}>
                          <td colSpan={4} style={{ padding: '8px 16px', fontWeight: 600, fontSize: 12 }}>Subtotal</td>
                          <td style={{ padding: '8px 16px', fontFamily: 'var(--mono)', textAlign: 'right', fontWeight: 600 }}>{fmtDec(bucket.subtotal)}</td>
                          <td style={{ padding: '8px 16px', fontFamily: 'var(--mono)', textAlign: 'right', fontWeight: 600, color: 'var(--brand)' }}>{fmtDec(bucket.taxTotal)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
